import React, { useMemo } from 'react';
import SectionCard from '../../components/SectionCard';

const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: d });
const fmtL = (n) => `${fmtNum(n, 1)} L`;
const fmtCop = (n) => `$ ${Math.round(Number(n || 0)).toLocaleString('es-CO')}`;

const ESTADO_BADGE = {
  sin_formula: { label: 'Sin fórmula', cls: 'bg-[#FEECEF] text-[#B71D3F]' },
  sin_consumo: { label: 'Sin consumo', cls: 'bg-[#FDF3E4] text-[#B87A18]' },
  en_rango: { label: 'En rango', cls: 'bg-[#E8F6F0] text-[#0E9F6E]' },
  sobre: { label: null, cls: 'bg-[#FEECEF] text-[#B71D3F]' },
};

const DiferenciaBar = ({ difL, maxAbs }) => {
  const pct = maxAbs > 0 ? Math.min((Math.abs(difL) / maxAbs) * 100, 100) : 0;
  const isExceso = difL > 0;
  return (
    <div className="w-[140px] ml-auto">
      <div className="relative h-2 bg-[#EEEEEE] dark:bg-gray-700 rounded-full">
        <div className="absolute left-1/2 -top-0.5 -bottom-0.5 w-px bg-[#C3C6D0]" />
        <div
          className="absolute top-0 h-2 rounded-full"
          style={
            isExceso
              ? { left: '50%', width: `${pct / 2}%`, background: '#FC536D' }
              : { right: '50%', width: `${pct / 2}%`, background: '#4A7BB7' }
          }
        />
      </div>
      <div className={`text-center text-xs font-bold font-['Manrope'] mt-1.5 ${isExceso ? 'text-[#B71D3F]' : 'text-[#2F6DAF]'}`}>
        {difL > 0 ? '+' : ''}{fmtL(difL)}
      </div>
    </div>
  );
};

const EstadoBadge = ({ estado, difPct }) => {
  const meta = ESTADO_BADGE[estado] || ESTADO_BADGE.en_rango;
  const label = meta.label || `${difPct > 0 ? '+' : ''}${difPct.toFixed(0)}%`;
  return (
    <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded-md whitespace-nowrap ${meta.cls}`}>
      {label}
    </span>
  );
};

// Tabla "producto por producto" (real vs. teórico, con costo y estado) + panel lateral
// "Programas de lavado" con la participación de cada programa en el consumo teórico.
const ProductoPorProductoSection = ({ merged, productosPorPrograma, litrosTeoricoTotal }) => {
  const maxAbsDif = useMemo(
    () => Math.max(...merged.map((p) => Math.abs(p.difL)), 1),
    [merged],
  );

  const totals = useMemo(
    () => merged.reduce(
      (acc, p) => ({
        realL: acc.realL + p.realL,
        teoricoL: acc.teoricoL + p.teoricoL,
        realCosto: acc.realCosto + p.realCosto,
      }),
      { realL: 0, teoricoL: 0, realCosto: 0 },
    ),
    [merged],
  );

  const programas = useMemo(
    () => (productosPorPrograma || []).map((prog) => ({
      ...prog,
      totalL: prog.totalMl / 1000,
      pct: litrosTeoricoTotal > 0 ? (prog.totalMl / 1000 / litrosTeoricoTotal) * 100 : 0,
    })),
    [productosPorPrograma, litrosTeoricoTotal],
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4 items-start">
      <SectionCard
        title="Producto por producto"
        hint="Inventario real contra lo que exigen las fórmulas cargadas en OptyLav."
      >
        {merged.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Sin datos en el periodo seleccionado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 pb-2">Producto</th>
                  <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Consumo real</th>
                  <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Teórico</th>
                  <th className="text-center text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Diferencia</th>
                  <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Costo del periodo</th>
                  <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 pb-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {merged.map((p) => (
                  <tr key={p.id} className="border-t border-[#EEEEEE] dark:border-gray-700">
                    <td className="py-3">
                      <div className="font-['Manrope'] font-bold text-sm text-[#001F41] dark:text-white">{p.nombre}</div>
                    </td>
                    <td className="text-right px-3 py-3 tabular-nums">
                      <span className="font-['Manrope'] font-bold text-sm text-[#001F41] dark:text-white">{fmtL(p.realL)}</span>
                    </td>
                    <td className="text-right px-3 py-3 tabular-nums">
                      <span className="font-['Manrope'] font-semibold text-sm text-[#43474F] dark:text-gray-400">{fmtL(p.teoricoL)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <DiferenciaBar difL={p.difL} maxAbs={maxAbsDif} />
                    </td>
                    <td className="text-right px-3 py-3 tabular-nums">
                      <span className="font-['Manrope'] font-bold text-sm text-[#001F41] dark:text-white">{fmtCop(p.realCosto)}</span>
                    </td>
                    <td className="text-right py-3">
                      <EstadoBadge estado={p.estado} difPct={p.difPct ?? 0} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-5 bg-[#001F41] rounded-xl px-6 py-4 flex items-center gap-7">
              <div className="flex-1">
                <div className="font-['Manrope'] font-bold text-[15px] text-white">Total químicos</div>
                <div className="text-[11.5px] text-white/55 mt-0.5">
                  {fmtL(totals.realL)} reales contra {fmtL(totals.teoricoL)} teóricos
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9.5px] tracking-wider uppercase text-white/45 mb-1">Costo real</div>
                <div className="font-['Manrope'] font-extrabold text-lg text-white">{fmtCop(totals.realCosto)}</div>
              </div>
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Programas de lavado" hint={`Dosificación teórica · ${fmtL(litrosTeoricoTotal)} en total.`}>
        {programas.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Sin programas con consumo teórico.</div>
        ) : (
          <div className="space-y-4">
            {programas.map((prog) => (
              <div key={prog.programaId || prog.programaNombre} className="pt-3 border-t border-[#EEEEEE] dark:border-gray-700 first:pt-0 first:border-t-0">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="font-['Manrope'] font-bold text-sm text-[#001F41] dark:text-white">{prog.programaNombre}</span>
                  <span className="font-['Manrope'] font-bold text-sm text-[#43474F] dark:text-gray-400">
                    {fmtL(prog.totalL)} · {prog.pct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-1.5 bg-[#EEEEEE] dark:bg-gray-700 rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full bg-[#0F3460]" style={{ width: `${prog.pct}%` }} />
                </div>
                {prog.productos.slice(0, 3).map((pr) => (
                  <div key={pr.id || pr.nombre} className="flex justify-between text-[11.5px] text-[#43474F] dark:text-gray-400 py-0.5">
                    <span>{pr.nombre}</span>
                    <b className="font-['Manrope'] font-semibold text-[#1A1C1C] dark:text-gray-200">{fmtL(pr.totalMl / 1000)}</b>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default ProductoPorProductoSection;
